import { newTheme } from '@customerio/pluma-components/css';
import type { Transition } from 'motion/react';

export function convertTimeStringToSeconds(timeString: string): number {
  const match = timeString.match(/^([\d.]+)\s*(ms|s)$/);
  if (match == null) {
    throw new Error(`Invalid time string: ${timeString}`);
  }
  const value = Number.parseFloat(match[1]);
  return match[2] === 'ms' ? value / 1000 : value;
}

export const STANDARD_PRODUCTIVE_EASING = newTheme.tokensMeta['motion-easing-standard-productive'].$value;
export const ENTRANCE_PRODUCTIVE_EASING = newTheme.tokensMeta['motion-easing-entrance-productive'].$value;
export const ENTRANCE_EXPRESSIVE_EASING = newTheme.tokensMeta['motion-easing-entrance-expressive'].$value;
export const EXIT_PRODUCTIVE_EASING = newTheme.tokensMeta['motion-easing-exit-productive'].$value;
export const FAST_01_DURATION = convertTimeStringToSeconds(newTheme.tokensMeta['motion-duration-fast-01'].$value);
export const FAST_02_DURATION = convertTimeStringToSeconds(newTheme.tokensMeta['motion-duration-fast-02'].$value);
export const MODERATE_01_DURATION = convertTimeStringToSeconds(
  newTheme.tokensMeta['motion-duration-moderate-01'].$value,
);
export const MODERATE_02_DURATION = convertTimeStringToSeconds(
  newTheme.tokensMeta['motion-duration-moderate-02'].$value,
);
export const SLOW_01_DURATION = convertTimeStringToSeconds(newTheme.tokensMeta['motion-duration-slow-01'].$value);
export const SLOW_02_DURATION = convertTimeStringToSeconds(newTheme.tokensMeta['motion-duration-slow-02'].$value);

export const MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION = {
  duration: MODERATE_01_DURATION,
  ease: STANDARD_PRODUCTIVE_EASING,
};
export const MODERATE_01_ENTRANCE_PRODUCTIVE_TRANSITION = {
  duration: MODERATE_01_DURATION,
  ease: ENTRANCE_PRODUCTIVE_EASING,
};
export const MODERATE_01_EXIT_PRODUCTIVE_TRANSITION = {
  duration: MODERATE_01_DURATION,
  ease: EXIT_PRODUCTIVE_EASING,
};
export const FAST_01_STANDARD_PRODUCTIVE_TRANSITION = {
  duration: FAST_01_DURATION,
  ease: STANDARD_PRODUCTIVE_EASING,
};
export const FAST_01_ENTRANCE_PRODUCTIVE_TRANSITION = {
  duration: FAST_01_DURATION,
  ease: ENTRANCE_PRODUCTIVE_EASING,
};
export const FAST_01_EXIT_PRODUCTIVE_TRANSITION = {
  duration: FAST_01_DURATION,
  ease: EXIT_PRODUCTIVE_EASING,
};

export const FAST_02_STANDARD_PRODUCTIVE_TRANSITION = {
  duration: FAST_02_DURATION,
  ease: STANDARD_PRODUCTIVE_EASING,
};
export const FAST_02_ENTRANCE_PRODUCTIVE_TRANSITION = {
  duration: FAST_02_DURATION,
  ease: ENTRANCE_PRODUCTIVE_EASING,
};
export const FAST_02_EXIT_PRODUCTIVE_TRANSITION = {
  duration: FAST_02_DURATION,
  ease: EXIT_PRODUCTIVE_EASING,
};

export const MODERATE_02_STANDARD_PRODUCTIVE_TRANSITION = {
  duration: MODERATE_02_DURATION,
  ease: STANDARD_PRODUCTIVE_EASING,
};
export const MODERATE_02_ENTRANCE_PRODUCTIVE_TRANSITION = {
  duration: MODERATE_02_DURATION,
  ease: ENTRANCE_PRODUCTIVE_EASING,
};
export const MODERATE_02_ENTRANCE_EXPRESSIVE_TRANSITION = {
  duration: MODERATE_02_DURATION,
  ease: ENTRANCE_EXPRESSIVE_EASING,
};
export const MODERATE_02_EXIT_PRODUCTIVE_TRANSITION = {
  duration: MODERATE_02_DURATION,
  ease: EXIT_PRODUCTIVE_EASING,
};

export const SLOW_01_STANDARD_PRODUCTIVE_TRANSITION = {
  duration: SLOW_01_DURATION,
  ease: STANDARD_PRODUCTIVE_EASING,
};
export const SLOW_01_ENTRANCE_PRODUCTIVE_TRANSITION = {
  duration: SLOW_01_DURATION,
  ease: ENTRANCE_PRODUCTIVE_EASING,
};
export const SLOW_01_EXIT_PRODUCTIVE_TRANSITION = {
  duration: SLOW_01_DURATION,
  ease: EXIT_PRODUCTIVE_EASING,
};

export const SLOW_02_STANDARD_PRODUCTIVE_TRANSITION = {
  duration: SLOW_02_DURATION,
  ease: STANDARD_PRODUCTIVE_EASING,
};
export const SLOW_02_ENTRANCE_PRODUCTIVE_TRANSITION = {
  duration: SLOW_02_DURATION,
  ease: ENTRANCE_PRODUCTIVE_EASING,
};
export const SLOW_02_EXIT_PRODUCTIVE_TRANSITION = {
  duration: SLOW_02_DURATION,
  ease: EXIT_PRODUCTIVE_EASING,
};

export const REDUCED_MOTION_TRANSITION = {
  duration: 0,
};

/** Vertical offset (px) for subtle entrance slides when motion is allowed. */
const ENTRANCE_SLIDE_Y_PX = 12;

/**
 * Opacity + slight upward slide for staggered entrances. Respects reduced motion (no offset, no duration).
 */
export function entranceFadeUpInitial(shouldReduceMotion: boolean | null): { opacity: number; y: number } {
  return shouldReduceMotion === true ? { opacity: 1, y: 0 } : { opacity: 0, y: ENTRANCE_SLIDE_Y_PX };
}

export const ENTRANCE_FADE_UP_ANIMATE = { opacity: 1, y: 0 } as const;

/**
 * Staggered expressive entrance using design-system duration + `FAST_02` spacing between steps.
 */
export function entranceStaggerTransition(staggerIndex: number, shouldReduceMotion: boolean | null): Transition {
  if (shouldReduceMotion === true) {
    return REDUCED_MOTION_TRANSITION;
  }
  return {
    ...MODERATE_02_ENTRANCE_EXPRESSIVE_TRANSITION,
    delay: staggerIndex * FAST_02_DURATION,
  };
}

/** One-shot expressive entrance (no stagger). Use when animating a single grouped block. */
export function entranceExpressiveTransition(shouldReduceMotion: boolean | null): Transition {
  if (shouldReduceMotion === true) {
    return REDUCED_MOTION_TRANSITION;
  }
  return MODERATE_02_ENTRANCE_EXPRESSIVE_TRANSITION;
}

/** Delay after the shell opens before portal content fades in (lets width settle first). */
export const SIDEBAR_PORTAL_FADE_IN_DELAY = FAST_02_DURATION * 0.5;

/**
 * Sidebar portal shell: fade only (no translate) so inner panels own vertical stagger — avoids stacked motion for accessibility and clarity.
 */
export function sidebarPortalFadeTransition(isClosing: boolean, shouldReduceMotion: boolean | null): Transition {
  if (shouldReduceMotion === true) {
    return REDUCED_MOTION_TRANSITION;
  }
  if (isClosing) {
    return { duration: 0, delay: 0 };
  }
  return {
    ...MODERATE_02_ENTRANCE_EXPRESSIVE_TRANSITION,
    delay: SIDEBAR_PORTAL_FADE_IN_DELAY,
  };
}

export function sidebarPortalFadeInitial(shouldReduceMotion: boolean | null): { opacity: number } {
  return shouldReduceMotion === true ? { opacity: 1 } : { opacity: 0 };
}

export const SIDEBAR_PORTAL_FADE_ANIMATE = { opacity: 1 } as const;
